/**
 * Global Search Keyboard Shortcut Hook
 *
 * Provides Cmd+K (Mac) / Ctrl+K (Windows/Linux) keyboard shortcut
 * to open the global search/assistant panel.
 *
 * Usage:
 *   useGlobalSearch(() => setAssistantOpen(true), enabled)
 */

import { useEffect, useCallback } from 'react'

/**
 * Hook to handle global search keyboard shortcut (Cmd+K / Ctrl+K).
 *
 * @param onOpen - Callback invoked when the shortcut is triggered
 * @param enabled - Whether the shortcut should intercept Cmd/Ctrl+K
 */
export function useGlobalSearch(onOpen: () => void, enabled = true): void {
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!enabled) return

      // Cmd+K (Mac) or Ctrl+K (Windows/Linux)
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        onOpen()
      }
    },
    [enabled, onOpen]
  )

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [handleKeyDown])
}
